import { DateTime } from 'luxon';

import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
  renderCheckNameCell,
  renderMatchResultCell,
  renderStatusCell,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import Pii from 'dashboard/components/Pii';
import { formatDate } from 'dashboard/lib/utils/formatters';
import {
  Check,
  CheckAttributeMatch,
  CheckName,
  DatabasePhoneCarrierAddressComparison,
  DatabasePhoneCarrierBirthdateComparison,
  DatabasePhoneCarrierNameFirstComparison,
  DatabasePhoneCarrierNameLastComparison,
} from 'dashboard/models/check';
import { VerificationDatabasePhoneCarrier } from 'dashboard/models/verification';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { sortChecks } from 'lib/utils/checks';

const ATTRIBUTE_CHECK_NAMES = [
  CheckName.DatabasePhoneCarrierNameFirstComparison,
  CheckName.DatabasePhoneCarrierNameLastComparison,
  CheckName.DatabasePhoneCarrierAddressComparison,
  CheckName.DatabasePhoneCarrierBirthdateComparison,
];

type DatabasePhoneCarrierCheck =
  | DatabasePhoneCarrierNameFirstComparison
  | DatabasePhoneCarrierNameLastComparison
  | DatabasePhoneCarrierAddressComparison
  | DatabasePhoneCarrierBirthdateComparison;

export const computeDatabasePhoneCarrierAttributeChecksMetadata = (
  verification: VerificationDatabasePhoneCarrier,
): CheckMetadata => {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);
  const isRedacted = verification.attributes?.['redacted-at'] != null;

  const rows = sortedChecks
    .filter((check): check is DatabasePhoneCarrierCheck =>
      ATTRIBUTE_CHECK_NAMES.includes(check.name),
    )
    .map((check) => {
      if (!ATTRIBUTE_CHECK_NAMES.includes(check.name)) {
        return null;
      }

      const checkDetails = CHECK_DISPLAY_CONFIG[check.name];
      if (checkDetails == null) {
        return null;
      }

      const reason = check.reasons?.[0];
      if (reason === 'disabled') {
        return null;
      }

      const metadata = check.metadata;
      if (metadata == null) {
        return null;
      }

      const matchResult = (metadata['match-result'] ||
        CheckAttributeMatch.Missing) as CheckAttributeMatch;
      const matchScore = metadata['match-score'];

      let reasonMissing = null;
      if (matchResult === CheckAttributeMatch.Missing) {
        switch (reason) {
          case 'external_service_error':
            reasonMissing = 'External service error';
            break;
          case 'unavailable_for_country':
            reasonMissing = 'Unavailable for country';
            break;
          default:
            reasonMissing = 'Missing';
            break;
        }
      }

      return [
        renderCheckNameCell(checkDetails.name, checkDetails.description),
        renderInputCell(check, verification, isRedacted),
        renderMatchResultCell(matchResult, undefined, metadata['match-level-minimum']),
        renderStatusCell(check.status),
        reasonMissing ?? matchScore,
      ];
    })
    .filter(Boolean);

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Carrier Match Results',
        },
        rowHeaders: ['Property', 'Input', 'Match Result', 'Status', 'Score'],
        rows,
        tableEmptyState: 'No comparison checks were performed for this verification.',
      },
    ] as CheckMetadataItem[],
  };
};

function renderInputCell(
  check: Check,
  verification: VerificationDatabasePhoneCarrier,
  isRedacted: boolean,
) {
  const {
    'name-first': nameFirst,
    'name-last': nameLast,
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-city': addressCity,
    'address-subdivision': addressSubdivision,
    'address-postal-code': addressPostalCode,
    birthdate,
  } = verification.attributes;

  switch (check.name) {
    case CheckName.DatabasePhoneCarrierNameFirstComparison:
      return <Pii isRedacted={isRedacted}>{nameFirst}</Pii>;
    case CheckName.DatabasePhoneCarrierNameLastComparison:
      return <Pii isRedacted={isRedacted}>{nameLast}</Pii>;
    case CheckName.DatabasePhoneCarrierAddressComparison:
      return (
        <Pii isRedacted={isRedacted}>
          <div>
            <div>
              {addressStreet1} {addressStreet2}
            </div>
            <div>
              {addressCity}, {addressSubdivision}, {addressPostalCode}
            </div>
          </div>
        </Pii>
      );
    case CheckName.DatabasePhoneCarrierBirthdateComparison:
      return (
        <Pii isRedacted={isRedacted}>
          {birthdate ? (
            <span>
              {formatDate(birthdate)} (
              {Math.floor(DateTime.local().diff(DateTime.fromISO(birthdate), 'years').years)} years
              old)
            </span>
          ) : (
            <></>
          )}
        </Pii>
      );
  }
}
